/**
 * Pure helpers for the CCM "smoothest UX" copy/move upgrade (handoff
 * `ccm-lexide-copy-move-smooth-ui-handoff-2026-06-30.md` §1.4 / §1.6).
 *
 * These sit ON TOP of the H#4 `copy-move-targets` primitives and encode three
 * load-bearing, drift-prone decisions so they're unit-testable in the
 * project's node env without a React mount:
 *
 *   1. DROP-TARGET VALIDITY (§1.1) — whether a given drag source may land on a
 *      given destination target. The scope matrix + current-scope exclusion
 *      already live in `buildCopyMoveTargets`; this is the per-pair predicate
 *      the drag overlay highlights with (a target built for THIS source is
 *      always valid, but the predicate also guards the org→org / same-case
 *      pairs defensively so a stale target list can't enable an illegal drop).
 *   2. COLLISION DETECTION (§1.6) — does the destination workspace tree
 *      already contain a file at the resource's canonical `sources/<name>`
 *      ref? The dest keeps the source basename (no rename in v1), so a
 *      same-basename `sources/` file in the dest is a replace.
 *   3. MOVE-UNDO INVERSION (§1.4) — the dest/source swap that turns a
 *      completed move into the reversing move. (Copy-undo is intentionally
 *      NOT here — there is no file-delete RPC; see the handoff §4.)
 */

import type { CopyMoveTarget, SourceScope } from "@/lib/copy-move-targets";
import type {
  ResourceCopyMoveInput,
} from "@/hooks/useResourceCopyMove";
import type { WorkspaceTreeFolder } from "@/types/gateway";

/**
 * Is `target` a valid drop destination for a resource sourced from `source`?
 *
 * Mirrors the server-side scope matrix + the H#4 exclude-current-scope rule:
 *   - `org → org` is rejected (never offer / accept it).
 *   - a case source onto the SAME case (same workspace) is rejected.
 *   - everything else in the allowed matrix (`case→case`, `case→org`,
 *     `org→case`) is valid.
 *
 * The drag overlay calls this per target to light up only the legal drop
 * zones — a belt-and-suspenders complement to `buildCopyMoveTargets` (which
 * already excludes the current scope when it builds the list).
 */
export function isValidDropTarget(
  source: SourceScope,
  target: CopyMoveTarget,
): boolean {
  if (target.kind === "org") {
    // org→org is illegal; case→org is fine.
    return source.scope !== "org";
  }
  // target is a case. A case→case onto the SAME case is illegal; org→case
  // (promotion) + case→(other)case are fine.
  if (source.scope === "case" && source.caseId === target.caseId) return false;
  return true;
}

/**
 * Walk a workspace tree to find a file whose canonical path equals
 * `path` (case-root-relative, e.g. "sources/contrato.pdf"). Returns the
 * matching file's path when present, else null.
 *
 * The dest keeps the source's `sources/<name>` ref verbatim (no rename in
 * v1), so a collision is an exact path match in the destination tree.
 */
export function findFileAtPath(
  root: WorkspaceTreeFolder | undefined,
  path: string,
): boolean {
  if (!root) return false;
  for (const f of root.files) {
    if (f.path === path) return true;
  }
  for (const sub of root.folders) {
    if (findFileAtPath(sub, path)) return true;
  }
  return false;
}

/**
 * Collision check (§1.6): would copying/moving the resource at `path` into
 * the destination whose (already-cached) tree is `destTree` REPLACE an
 * existing same-path file?
 *
 * The caller supplies the destination scope's cached workspace tree (keyed
 * by `targetKeySegment` in React-Query — `["case-workspace-tree", <seg>]`).
 * When the dest tree isn't cached yet, pass `undefined` → no known collision
 * (the picker shows the at-a-glance collision NOTE either way; this only
 * drives the pre-flight CONFIRM when we positively know the file exists).
 */
export function destinationHasCollision(
  destTree: WorkspaceTreeFolder | undefined,
  path: string,
): boolean {
  return findFileAtPath(destTree, path);
}

/**
 * Invert a COMPLETED move into the reversing move (§1.4). The original op
 * moved `path` from `source` → `target`; the undo moves the SAME canonical
 * `sources/<name>` ref back from the target scope → the original source
 * scope.
 *
 * Builds a fresh `ResourceCopyMoveInput` with:
 *   - `mode: "move"` (always — undo of a move is a move).
 *   - `source` = the ORIGINAL TARGET's scope address (where the resource now
 *     lives). A case target carries its full triple; the org target is
 *     `{scope:"org"}`.
 *   - `target` = a target rebuilt from the ORIGINAL SOURCE scope. A case
 *     source needs its full triple to address back — the caller supplies it
 *     via `originalSource` (the active selection had it).
 *
 * Pure + total: throws nothing; returns the inverted input. The path is
 * unchanged (the dest kept the canonical ref).
 *
 * NOTE: copy-undo is deliberately absent — undoing a copy would require a
 * file-delete RPC that does not exist (handoff §4). The orchestrator gates
 * the Undo affordance on `mode === "move"`.
 */
export function invertMove(
  done: {
    /** The resource's canonical path (unchanged across the move). */
    path: string;
    /** The ORIGINAL source scope (full triple for a case). */
    originalSource: ResourceCopyMoveInput["source"];
    /** The ORIGINAL destination target the resource was moved INTO. */
    originalTarget: CopyMoveTarget;
  },
): ResourceCopyMoveInput {
  // The resource now lives in the original TARGET — that becomes the undo's
  // source scope.
  const undoSource: ResourceCopyMoveInput["source"] =
    done.originalTarget.kind === "org"
      ? { scope: "org" }
      : {
          scope: "case",
          clientId: done.originalTarget.address.clientId,
          matterId: done.originalTarget.address.matterId,
          caseId: done.originalTarget.address.caseId,
        };

  // The undo's destination is the original SOURCE scope, expressed as a
  // CopyMoveTarget so it threads straight back through useResourceCopyMove.
  const undoTarget: CopyMoveTarget =
    done.originalSource.scope === "org"
      ? { kind: "org", caseId: "org", title: "", address: { scope: "org" } }
      : {
          kind: "case",
          caseId: done.originalSource.caseId,
          title: "",
          address: {
            scope: "case",
            clientId: done.originalSource.clientId,
            matterId: done.originalSource.matterId,
            caseId: done.originalSource.caseId,
          },
        };

  return {
    mode: "move",
    path: done.path,
    source: undoSource,
    target: undoTarget,
  };
}
